import { Typography, makeStyles, withStyles } from "@material-ui/core";

import { FC } from "react";
import LocaleLink from "../Navigation/LocaleLink";
import { TheCycleverseTheme } from "src/theme";
import clsx from "clsx";

const useH1Styles = makeStyles<TheCycleverseTheme, { color?: string }>((theme) => ({
  root: {
    ...theme.headline2,
    fontWeight: 300,
    textAlign: "center",
    color: ({ color }) => color || "inherit",
    marginTop: theme.spacing(4),
    [theme.breakpoints.down("xs")]: {
      fontSize: 24,
    },
  },
}));

export const PreCalculatorHeadline: FC<{ tag?: string; color?: string }> = ({ children, tag, color }) => {
  const classes = useH1Styles({ color });
  return tag === "h2" ? <h2 className={classes.root}>{children}</h2> : <h1 className={classes.root}>{children}</h1>;
};

// ------------------------------------------------------

const useH1LeftStyles = makeStyles((theme: TheCycleverseTheme) => ({
  root: {
    ...theme.headline2,
    fontWeight: 300,
    marginTop: theme.spacing(4),
  },
}));

export const CalculatorHeadlineLeft: FC = ({ children }) => {
  const classes = useH1LeftStyles();
  return <h2 className={classes.root}>{children}</h2>;
};

// ------------------------------------------------------
const useH2Styles = makeStyles((theme: TheCycleverseTheme) => ({
  root: (centered) => {
    return {
      fontSize: 36,
      marginTop: 0,
      fontFamily: theme.highlightFontFamily,
      fontWeight: 900,
      maxWidth: 900,
      textAlign: "center",
      display: centered ? "block" : "inline-block",
      lineHeight: "1.1",
      [theme.breakpoints.down("xs")]: {
        fontSize: 28,
      },
    };
  },
}));

export const CalculatorHeadline: FC<{ centered?: boolean }> = ({ centered = false, children }) => {
  const classes = useH2Styles(centered);
  return <h2 className={classes.root}>{children}</h2>;
};

// ------------------------------------------------------
const useTopicHeadlineStyles = makeStyles<
  TheCycleverseTheme,
  {
    color?: string;
  }
>((theme) => ({
  root: {
    marginTop: theme.spacing(4),
    fontSize: 24,
    fontFamily: "inherit",
    fontWeight: 300,
    color: ({ color }) => color || "inherit",
    lineHeight: "1.1",
  },
}));

export const CalculatorTopicHeadline: FC<{ color?: string }> = ({ children, color }) => {
  const classes = useTopicHeadlineStyles({ color });
  return <h3 className={classes.root}>{children}</h3>;
};

// ------------------------------------------------------
const useInputHeadlineStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    fontSize: 24,
    fontFamily: "inherit",
    fontWeight: 300,
    lineHeight: "1.1",
  },
}));

export const CalculatorInputHeadline: FC = ({ children }) => {
  const classes = useInputHeadlineStyles();
  return <div className={classes.root}>{children}</div>;
};

// ------------------------------------------------------
const useTopicH2HeadlineStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(4),
    marginBottom: "5px",
    fontSize: 24,
    fontFamily: "inherit",
    fontWeight: 300,
    lineHeight: "1.1",
  },
}));

export const CalculatorTopicH2Headline: FC<{ className?: string }> = ({ children, className }) => {
  const classes = useTopicH2HeadlineStyles();
  return <h2 className={className ? className : classes.root}>{children}</h2>;
};

// ------------------------------------------------------
const useTopicH3HeadlineAltStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(4),
    marginBottom: "0px",
    fontSize: 24,
    fontFamily: "inherit",
    fontWeight: 300,
    lineHeight: "26px",
  },
}));

export const CalculatorTopicH3Headline: FC<{ className?: string }> = ({ children, className }) => {
  const classes = useTopicH3HeadlineAltStyles();
  return <h3 className={className ? className : classes.root}>{children}</h3>;
};

// ------------------------------------------------------
const useCaptionStyles = makeStyles((theme: TheCycleverseTheme) => ({
  root: {
    fontSize: "32px",
    fontFamily: "DM Serif Text Regular",
    lineHeight: 1.1,
  },
}));

export const CalculatorCaption: FC = ({ children }) => {
  const classes = useCaptionStyles();
  return <h2 className={classes.root}>{children}</h2>;
};

// ------------------------------------------------------
const useElevatedTextStyles = makeStyles((theme: TheCycleverseTheme) => ({
  root: {
    fontSize: "medium",
    fontWeight: 900,
  },
}));

export const CalculatorElevatedText: FC = ({ children }) => {
  const classes = useElevatedTextStyles();
  return <span className={classes.root}>{children}</span>;
};

// ------------------------------------------------------
const useProminentTextStyles = makeStyles((theme: TheCycleverseTheme) => ({
  root: {
    ...theme.headline2,
    fontWeight: 300,
    textAlign: "center",
    marginTop: theme.spacing(4),
    [theme.breakpoints.down("xs")]: {
      fontSize: 20,
    },
  },
}));

export const CalculatorProminentText: FC = ({ children }) => {
  const classes = useProminentTextStyles();
  return <span className={classes.root}>{children}</span>;
};

// ------------------------------------------------------
const useTextHighlightStyles = makeStyles((theme: TheCycleverseTheme) => ({
  root: {
    color: theme.saleColor,
  },
}));

export const CalculatorTextHighlight: FC = ({ children }) => {
  const classes = useTextHighlightStyles();
  return <span className={classes.root}>{children}</span>;
};

// ------------------------------------------------------

const useBodyTextStyles = makeStyles<
  TheCycleverseTheme,
  {
    color?: string;
  }
>((theme) => ({
  root: {
    fontSize: "medium !important",
    "& b,strong": {
      fontWeight: 900,
    },
    color: ({ color }) => color || "inherit",
  },
}));

export const CalculatorBodyText: FC<{ className?: string; color?: string }> = ({ children, className, color }) => {
  const classes = useBodyTextStyles({ color });
  return <p className={`${classes.root} ${className}`}>{children}</p>;
};

// ------------------------------------------------------

const useImageDescriptionTextStyles = makeStyles<
  TheCycleverseTheme,
  {
    color?: string;
  }
>((theme) => ({
  root: {
    fontSize: "medium !important",
    "& b,strong": {
      fontWeight: 300,
    },
    color: ({ color }) => color || "#8b8b8b",
  },
}));

export const CalculatorImageDescriptionText: FC<{ className?: string; color?: string }> = ({
  children,
  className,
  color,
}) => {
  const classes = useImageDescriptionTextStyles({ color });
  return <p className={`${classes.root} ${className}`}>{children}</p>;
};

// ------------------------------------------------------
const useBodyTextWithColorStyles = makeStyles(() => ({
  root: (color) => {
    return { fontSize: "medium", fontWeight: 600, color: color ? "#fd4901" : "" };
  },
}));

export const CalculatorBodyTextWithColor: FC<{ color?: boolean }> = ({ children, color }) => {
  const classes = useBodyTextWithColorStyles(color);
  return <div className={classes.root}>{children}</div>;
};

// ------------------------------------------------------
const useInputCaptionStyles = makeStyles((theme: TheCycleverseTheme) => ({
  root: {
    fontSize: "medium",
    "& b,strong": {
      fontWeight: 900,
    },
  },
}));

export const InputCaption: FC = ({ children }) => {
  const classes = useInputCaptionStyles();
  return <div className={classes.root}>{children}</div>;
};

const useTextCenterStyles = makeStyles({
  root: {
    textAlign: "center",
  },
});

type ClassName = {
  className?: string;
};

export const CalculatorTextCenter: FC<ClassName> = (props) => {
  const classes = useTextCenterStyles();
  const { className } = props;
  return <div className={clsx(classes.root, className)}>{props.children}</div>;
};

const useListItemStyles = makeStyles({
  listItem: {
    display: "flex",
    fontSize: "16px",
    fontWeight: 300,
  },
  listPoint: {
    backgroundColor: "black",
    width: "5px",
    height: "5px",
    marginRight: "10px",
    borderRadius: "2.5px",
    marginTop: "8.9375px",
  },
  listText: {},
});

export const CalculatorListItem: FC = ({ children }) => {
  const classes = useListItemStyles();
  return (
    <div className={classes.listItem}>
      <div className={classes.listPoint} />
      <div className={classes.listText}>{children}</div>
    </div>
  );
};

// ------------------------------------------------------
const boldTextStyles = makeStyles((theme: TheCycleverseTheme) => ({
  paragraphWithDifferentSyles: {
    fontSize: "16px",
    fontWeight: 600,
    fontFamily: theme.typography.fontFamily,
    lineHeight: "24px",
    margin: 0,
  },
}));

export const BoldParagraphWithOrangeText: FC = ({ children }) => {
  const classes = boldTextStyles();
  return <h3 className={classes.paragraphWithDifferentSyles}>{children}</h3>;
};

// ------------------------------------------------------
const h3HeadlineStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(4),
    marginBottom: 0,
    fontSize: 24,
    fontFamily: "inherit",
    fontWeight: 300,
    lineHeight: "1.1",
  },
}));

export const H3Headline: FC = ({ children }) => {
  const classes = h3HeadlineStyles();
  return <h3 className={classes.root}>{children}</h3>;
};

const listLinkStyles = makeStyles((theme: TheCycleverseTheme) => ({
  listLink: {
    color: theme.grayColor,
    fontSize: "18px",
  },
}));

export const ListLinkText: FC<{ linkName?: string; link?: string }> = ({ linkName, link }) => {
  const classes = listLinkStyles();
  return (
    <span>
      <a className={classes.listLink} href={link}>
        {linkName}
      </a>
    </span>
  );
};

// ------------------------------------------------------
const useSliderLabelStyles = makeStyles((theme: TheCycleverseTheme) => ({
  root: {
    color: theme?.sliderLabelColor,
  },
}));

export const SliderLabel: FC = ({ children }) => {
  const classes = useSliderLabelStyles();
  return <span className={classes.root}>{children}</span>;
};
